import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiPlusBold, PiPencilSimpleBold, PiTrashBold, PiUsersBold,
    PiMagnifyingGlassBold, PiTreeStructureBold, PiArrowCounterClockwise,
} from 'react-icons/pi';
import { Badge, Button, PageHeader, LoadingState } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { SalesTeam, SalesTeamMemberRow, SellerAssignmentHistory, SellerAssignmentPeriod } from './types';
import { ROLE_LABELS } from './types';

const AVATAR_FALLBACK = '/img/favicon.png';
const EFFECTIVE_EPOCH = '2000-01-01';

function isoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function firstDayOfMonth(): string {
    const d = new Date();
    return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function yesterdayIso(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return isoDate(d);
}

function dayBefore(iso: string): string {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() - 1);
    return isoDate(d);
}

function formatIsoDate(value: string | null | undefined, fallback: string): string {
    if (!value) return fallback;
    const [y, m, d] = value.split('-');
    if (!y || !m || !d) return value;
    return `${d}/${m}/${y}`;
}

function isEpoch(value: string | null | undefined): boolean {
    return !value || value === EFFECTIVE_EPOCH;
}

function initials(name: string) {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join('');
}

function UserAvatar({
    name,
    avatarUrl,
    size = 'md',
}: {
    name: string;
    avatarUrl?: string | null;
    size?: 'sm' | 'md';
}) {
    const dim = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs';

    if (avatarUrl) {
        return (
            <img
                src={avatarUrl}
                alt={name}
                className={`${dim} shrink-0 rounded-full object-cover ring-1 ring-gray-200 dark:ring-zinc-700`}
                onError={(e) => {
                    (e.target as HTMLImageElement).src = AVATAR_FALLBACK;
                }}
            />
        );
    }

    return (
        <span className={`${dim} flex shrink-0 items-center justify-center rounded-full bg-orange-100 font-bold text-orange-700 dark:bg-orange-950 dark:text-orange-300`}>
            {initials(name)}
        </span>
    );
}

type RosterUser = {
    id: number;
    name: string;
    email: string;
    job_title: string | null;
    avatar_url?: string | null;
    assignment: {
        team_id: number;
        team_name: string;
        manager_name: string | null;
        commission_role: string;
    } | null;
};

type Translate = (key: string, replace?: Record<string, string | number>) => string;

type TeamSavePayload = {
    team: Record<string, unknown>;
    members: Partial<SalesTeamMemberRow>[];
    unassigned?: { user_id: number; effective_to: string }[];
};

function isSellerLike(u: RosterUser): boolean {
    const title = (u.job_title ?? '').toLowerCase();
    return title.includes('vendedor') || title.includes('sdr');
}

function memberSinceLabel(from: string | null | undefined, t: Translate): string {
    if (isEpoch(from)) {
        return t('v2.commissioning.sales_teams.open_ended');
    }

    return t('v2.commissioning.sales_teams.since', { date: formatIsoDate(from, from ?? '') });
}

function periodFromLabel(from: string | null | undefined, t: Translate): string {
    if (isEpoch(from)) {
        return t('v2.commissioning.sales_teams.since_start');
    }

    return formatIsoDate(from, '—');
}

function periodRangeLabel(period: SellerAssignmentPeriod, t: Translate): string {
    const to = period.to
        ? formatIsoDate(period.to, '—')
        : t('v2.commissioning.sales_teams.open_ended');

    return `${periodFromLabel(period.from, t)} → ${to}`;
}

function currentAssignment(periods: SellerAssignmentPeriod[]): SellerAssignmentPeriod | undefined {
    const assigned = periods.filter((p) => !p.is_gap);

    return assigned.find((p) => p.is_open || !p.to) ?? assigned.at(-1);
}

function DateField({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <label className="block min-w-[10rem]">
            <span className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-zinc-400">{label}</span>
            <input
                type="date"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </label>
    );
}


// ---------------------------------------------------------------------------
// MemberEditor — seção completa (vendedores existentes + busca + novos)
// ---------------------------------------------------------------------------

function MemberEditor({
    roster,
    selectedIds,
    onToggle,
    teamId,
    managerId,
    existingMembers,
    fromByUser,
    untilByUser,
    onFromChange,
    onUntilChange,
}: {
    roster: RosterUser[];
    selectedIds: number[];
    onToggle: (id: number) => void;
    teamId?: number;
    managerId: number | null;
    existingMembers: SalesTeamMemberRow[];
    fromByUser: Record<number, string>;
    untilByUser: Record<number, string>;
    onFromChange: (userId: number, value: string) => void;
    onUntilChange: (userId: number, value: string) => void;
}) {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const rosterById = useMemo(() => new Map(roster.map((u) => [u.id, u])), [roster]);

    // Vendedores com período existente no banco (vêm via existingMembers agrupados)
    const existingUserIds = useMemo(() => {
        const ids = new Set<number>();
        existingMembers.forEach((m) => {
            if (m.commission_role === 'vendedor' || m.commission_role === 'sdr') {
                ids.add(m.user_id);
            }
        });
        return ids;
    }, [existingMembers]);

    const existingBySeller = useMemo(() => {
        const map = new Map<number, SalesTeamMemberRow[]>();
        existingMembers.forEach((m) => {
            if (m.commission_role === 'vendedor' || m.commission_role === 'sdr') {
                if (!map.has(m.user_id)) map.set(m.user_id, []);
                map.get(m.user_id)!.push(m);
            }
        });
        return map;
    }, [existingMembers]);

    // Vendedores novos (adicionados via busca nesta sessão, ainda não salvos no banco)
    const newSellerIds = selectedIds.filter((id) => !existingUserIds.has(id));

    // Vendedores saindo (tinham período aberto, foram removidos via toggle)
    const leaving = Object.keys(untilByUser)
        .map((id) => Number(id))
        .filter((id) => !selectedIds.includes(id) && existingUserIds.has(id))
        .map((id) => rosterById.get(id))
        .filter((u): u is RosterUser => Boolean(u));

    const candidates = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length < 1) return [];

        return roster.filter((u) => {
            if (managerId && u.id === managerId) return false;
            if (existingUserIds.has(u.id)) return false;
            if (selectedIds.includes(u.id)) return false;
            if (untilByUser[u.id]) return false;
            if (!isSellerLike(u)) return false;
            return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
        });
    }, [roster, search, managerId, selectedIds, untilByUser, existingUserIds]);

    return (
        <div className="space-y-5">
            {/* Vendedores existentes — repeater de períodos */}
            <div>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-zinc-200">
                        {t('v2.commissioning.sales_teams.section_current')}
                    </h4>
                    <span className="text-xs text-gray-500">
                        {t('v2.commissioning.sales_teams.members_count', { count: existingUserIds.size })}
                    </span>
                </div>
                {existingUserIds.size === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400 dark:border-zinc-700">
                        {t('v2.commissioning.sales_teams.no_sellers_linked')}
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 dark:divide-zinc-800 dark:border-zinc-700">
                        {Array.from(existingBySeller.entries()).map(([userId, periods]) => {
                            const rosterUser = rosterById.get(userId);
                            const firstPeriod = periods[0];
                            const name = firstPeriod.user_name ?? rosterUser?.name ?? String(userId);
                            return (
                                <li key={userId} className="flex items-center gap-3 bg-white px-3 py-3 dark:bg-zinc-900">
                                    <UserAvatar name={name} avatarUrl={firstPeriod.user_avatar_url ?? rosterUser?.avatar_url} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-medium text-gray-900 dark:text-zinc-100">{name}</p>
                                        <p className="truncate text-xs text-gray-500">{rosterUser?.job_title || firstPeriod.user_email}</p>
                                    </div>
                                    <Link
                                        to={`/commissioning/teams/sellers/${userId}/periods`}
                                        className="shrink-0 text-xs font-medium text-orange-600 hover:text-orange-700"
                                    >
                                        {t('v2.commissioning.sales_teams.manage_periods')}
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={() => onToggle(userId)}
                                        className="shrink-0 text-xs text-gray-400 hover:text-red-600"
                                    >
                                        {t('v2.commissioning.sales_teams.remove_seller')}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Vendedores saindo */}
            {leaving.length > 0 && (
                <div>
                    <h4 className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
                        {t('v2.commissioning.sales_teams.section_leaving')}
                    </h4>
                    <ul className="space-y-2">
                        {leaving.map((u) => (
                            <li
                                key={u.id}
                                className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/60 dark:bg-amber-950/30"
                            >
                                <UserAvatar name={u.name} avatarUrl={u.avatar_url} size="sm" />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-gray-900 dark:text-zinc-100">{u.name}</p>
                                    <p className="text-[11px] text-amber-800 dark:text-amber-200">
                                        {t('v2.commissioning.sales_teams.sales_until_help')}
                                    </p>
                                </div>
                                <DateField
                                    label={t('v2.commissioning.sales_teams.sales_until')}
                                    value={untilByUser[u.id]}
                                    onChange={(value) => onUntilChange(u.id, value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => onToggle(u.id)}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900 dark:text-amber-200"
                                >
                                    <PiArrowCounterClockwise className="size-3.5" />
                                    {t('v2.commissioning.sales_teams.undo_leave')}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Novos vendedores adicionados nesta sessão (ainda não persistidos) */}
            {newSellerIds.length > 0 && (
                <div>
                    <h4 className="mb-2 text-sm font-semibold text-orange-700 dark:text-orange-300">
                        {t('v2.commissioning.sales_teams.section_add')}
                    </h4>
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-orange-200 dark:divide-zinc-800 dark:border-orange-900/60">
                        {newSellerIds.map((id) => {
                            const u = rosterById.get(id);
                            if (!u) return null;
                            return (
                                <li key={id} className="flex flex-wrap items-center gap-3 bg-orange-50/40 px-3 py-3 dark:bg-orange-950/10">
                                    <UserAvatar name={u.name} avatarUrl={u.avatar_url} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-medium text-gray-900 dark:text-zinc-100">{u.name}</p>
                                        <p className="truncate text-xs text-gray-500">{u.job_title || u.email}</p>
                                    </div>
                                    <DateField
                                        label={t('v2.commissioning.sales_teams.period_from')}
                                        value={fromByUser[id] ?? firstDayOfMonth()}
                                        onChange={(value) => onFromChange(id, value)}
                                    />
                                    {u.assignment && u.assignment.team_id !== teamId && (
                                        <p className="w-full text-[11px] text-amber-700 dark:text-amber-300">
                                            {t('v2.commissioning.sales_teams.closes_previous', {
                                                team: u.assignment.team_name,
                                                date: formatIsoDate(dayBefore(fromByUser[id] ?? firstDayOfMonth()), fromByUser[id] ?? ''),
                                            })}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onToggle(id)}
                                        className="shrink-0 text-xs text-gray-400 hover:text-red-600"
                                    >
                                        {t('v2.commissioning.sales_teams.remove_seller')}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Busca para adicionar novo vendedor */}
            <div>
                <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-zinc-200">
                    {t('v2.commissioning.sales_teams.section_add')}
                </h4>
                <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700">
                    <PiMagnifyingGlassBold className="size-4 shrink-0 text-gray-400" />
                    <input
                        className="flex-1 bg-transparent text-sm outline-none dark:text-zinc-200"
                        placeholder={t('v2.commissioning.sales_teams.search_seller')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {search.trim().length < 1 ? (
                    <p className="text-xs text-gray-400">{t('v2.commissioning.sales_teams.search_to_add')}</p>
                ) : candidates.length === 0 ? (
                    <p className="text-xs text-gray-400">{t('v2.commissioning.sales_teams.no_sellers')}</p>
                ) : (
                    <ul className="max-h-56 space-y-1 overflow-y-auto">
                        {candidates.map((u) => {
                            const assignedElsewhere = u.assignment
                                && u.assignment.team_id !== teamId
                                && u.assignment.commission_role === 'vendedor';
                            return (
                                <li
                                    key={u.id}
                                    className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-gray-50 dark:hover:bg-zinc-800/60"
                                >
                                    <UserAvatar name={u.name} avatarUrl={u.avatar_url} size="sm" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-gray-900 dark:text-zinc-100">{u.name}</p>
                                        {assignedElsewhere ? (
                                            <p className="truncate text-[11px] font-medium text-amber-600">
                                                {t('v2.commissioning.sales_teams.on_team', {
                                                    name: u.assignment?.manager_name ?? u.assignment?.team_name ?? '',
                                                })}
                                            </p>
                                        ) : (
                                            <p className="truncate text-[11px] text-gray-500">{u.job_title || u.email}</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onToggle(u.id);
                                            setSearch('');
                                        }}
                                        className="text-xs font-medium text-orange-600 hover:text-orange-700"
                                    >
                                        {t('v2.commissioning.sales_teams.include_seller')}
                                    </button>
                                </li>
                        );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

function TeamForm({
    initial,
    roster,
    directorDefaultId,
    onSave,
    onCancel,
    loading,
}: {
    initial?: Partial<SalesTeam>;
    roster: RosterUser[];
    directorDefaultId: number | null;
    onSave: (data: TeamSavePayload) => void;
    onCancel: () => void;
    loading: boolean;
}) {
    const { t } = useI18n();
    const [team, setTeam] = useState({
        name: initial?.name ?? '',
        commission_mode: initial?.commission_mode ?? 'standard' as 'standard' | 'fixed_team',
        manager_id: initial?.manager?.id ?? null as number | null,
        director_id: initial?.director?.id ?? directorDefaultId,
        active: initial?.active ?? true,
        notes: initial?.notes ?? null as string | null,
    });

    // Membros existentes no banco (todos os períodos)
    const existingMembers = initial?.members ?? [];

    // IDs de vendedores existentes com período aberto — usados para o toggle de saída
    const openExistingSellerIds = useMemo(() => {
        return (initial?.members ?? [])
            .filter((m) => (m.commission_role === 'vendedor' || m.commission_role === 'sdr') && !m.effective_to)
            .map((m) => m.user_id);
    }, [initial]);

    // sellerIds gerencia apenas NOVOS vendedores adicionados nesta sessão
    const [sellerIds, setSellerIds] = useState<number[]>([]);
    const [members, setMembers] = useState<Partial<SalesTeamMemberRow>[]>(initial?.members ?? []);
    const [fromByUser, setFromByUser] = useState<Record<number, string>>({});
    const [untilByUser, setUntilByUser] = useState<Record<number, string>>({});

    const managers = roster.filter((u) =>
        CommercialManagerEmails.has(u.email) || (u.job_title ?? '').toLowerCase().includes('gerente'),
    );
    const directors = roster.filter((u) =>
        u.email === 'paulo.benicio@bucklerfit.com' || (u.job_title ?? '').toLowerCase().includes('diretor'),
    );

    const addMember = () => setMembers((p) => [...p, {
        commission_role: 'vendedor',
        user_id: undefined,
        sdr_id: undefined,
        fixed_rate: undefined,
        sdr_share_rate: undefined,
        is_dual_role: false,
    }]);
    const removeMember = (i: number) => setMembers((p) => p.filter((_, idx) => idx !== i));
    const updateMember = (i: number, field: string, value: unknown) =>
        setMembers((p) => p.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));

    const handleSave = () => {
        if (team.commission_mode === 'standard') {
            const built: Partial<SalesTeamMemberRow>[] = [];
            if (team.manager_id) {
                built.push({ user_id: team.manager_id, commission_role: 'gerente' });
            }
            // Inclui vendedores existentes (apenas os abertos para manter gerente/campos)
            openExistingSellerIds.forEach((id) => {
                if (!untilByUser[id]) {
                    built.push({ user_id: id, commission_role: 'vendedor' });
                }
            });
            // Novos vendedores adicionados nesta sessão
            sellerIds.forEach((id) => {
                const row: Partial<SalesTeamMemberRow> = { user_id: id, commission_role: 'vendedor' };
                row.effective_from = fromByUser[id] ?? firstDayOfMonth();
                built.push(row);
            });
            const unassigned = Object.entries(untilByUser)
                .filter(([id]) => !sellerIds.includes(Number(id)))
                .map(([id, effective_to]) => ({ user_id: Number(id), effective_to }));
            onSave({ team, members: built, unassigned });
            return;
        }
        onSave({ team, members });
    };

    const toggleSeller = (id: number) => {
        // Vendedor existente no banco sendo removido
        if (openExistingSellerIds.includes(id)) {
            setUntilByUser((p) => {
                if (p[id]) {
                    // Desfazer saída
                    const next = { ...p };
                    delete next[id];
                    return next;
                }
                return { ...p, [id]: yesterdayIso() };
            });
            return;
        }

        // Novo vendedor (adicionado nesta sessão)
        if (sellerIds.includes(id)) {
            setSellerIds((ids) => ids.filter((x) => x !== id));
            setFromByUser((p) => {
                const next = { ...p };
                delete next[id];
                return next;
            });
            return;
        }

        setSellerIds((ids) => [...ids, id]);
        setFromByUser((p) => ({ ...p, [id]: firstDayOfMonth() }));
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
                <div className="space-y-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">{t('v2.commissioning.sales_teams.name')}</label>
                        <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                            value={team.name}
                            onChange={(e) => setTeam((p) => ({ ...p, name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">{t('v2.commissioning.sales_teams.mode')}</label>
                        <select
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                            value={team.commission_mode}
                            onChange={(e) => setTeam((p) => ({ ...p, commission_mode: e.target.value as 'standard' | 'fixed_team' }))}
                        >
                            <option value="standard">{t('v2.commissioning.sales_teams.mode_standard')}</option>
                            <option value="fixed_team">{t('v2.commissioning.sales_teams.mode_fixed')}</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">{t('v2.commissioning.sales_teams.manager')}</label>
                        <select
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                            value={team.manager_id ?? ''}
                            onChange={(e) => {
                                const id = e.target.value ? parseInt(e.target.value) : null;
                                const mgr = managers.find((m) => m.id === id);
                                setTeam((p) => ({
                                    ...p,
                                    manager_id: id,
                                    name: p.name || (mgr ? `Equipe ${mgr.name}` : p.name),
                                }));
                            }}
                        >
                            <option value="">{t('v2.commissioning.sales_teams.manager_placeholder')}</option>
                            {managers.map((u) => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">{t('v2.commissioning.sales_teams.director')}</label>
                        <select
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                            value={team.director_id ?? ''}
                            onChange={(e) => setTeam((p) => ({ ...p, director_id: e.target.value ? parseInt(e.target.value) : null }))}
                        >
                            <option value="">{t('v2.commissioning.sales_teams.director_none')}</option>
                            {directors.map((u) => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={team.active} onChange={(e) => setTeam((p) => ({ ...p, active: e.target.checked }))} className="rounded" />
                        {t('v2.commissioning.sales_teams.active')}
                    </label>
                </div>

                {team.commission_mode === 'standard' ? (
                    <MemberEditor
                        roster={roster}
                        selectedIds={sellerIds}
                        onToggle={toggleSeller}
                        teamId={initial?.id}
                        managerId={team.manager_id}
                        existingMembers={existingMembers}
                        fromByUser={fromByUser}
                        untilByUser={untilByUser}
                        onFromChange={(userId, value) => setFromByUser((p) => {
                            const next = { ...p };
                            if (!value) { delete next[userId]; return next; }
                            next[userId] = value;
                            return next;
                        })}
                        onUntilChange={(userId, value) => setUntilByUser((p) => ({ ...p, [userId]: value }))}
                    />
                ) : (
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-700">{t('v2.commissioning.sales_teams.sellers')}</h4>
                            <button type="button" onClick={addMember} className="flex items-center gap-1 text-xs text-orange-600">
                                <PiPlusBold className="h-3 w-3" /> {t('v2.commissioning.sales_teams.add_member')}
                            </button>
                        </div>
                        <div className="space-y-2">
                            {members.map((m, i) => (
                                <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-2">
                                    <select
                                        className="min-w-[140px] flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                                        value={m.user_id ?? ''}
                                        onChange={(e) => updateMember(i, 'user_id', e.target.value ? parseInt(e.target.value) : undefined)}
                                    >
                                        <option value="">…</option>
                                        {roster.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                    <select
                                        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                                        value={m.commission_role ?? 'vendedor'}
                                        onChange={(e) => updateMember(i, 'commission_role', e.target.value)}
                                    >
                                        {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'override').map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        placeholder="%"
                                        value={m.fixed_rate != null ? (m.fixed_rate * 100).toFixed(2) : ''}
                                        onChange={(e) => updateMember(i, 'fixed_rate', e.target.value ? parseFloat(e.target.value) / 100 : undefined)}
                                        className="w-24 rounded border border-gray-300 px-2 py-1.5 text-sm"
                                    />
                                    <button type="button" onClick={() => removeMember(i)} className="p-1 text-red-400 hover:text-red-600">
                                        <PiTrashBold className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-zinc-800">
                <Button variant="outline" onClick={onCancel}>{t('v2.commissioning.sales_teams.cancel')}</Button>
                <Button
                    onClick={handleSave}
                    disabled={loading || !team.name || (team.commission_mode === 'standard' && !team.manager_id)}
                    isLoading={loading}
                >
                    {t('v2.commissioning.sales_teams.save')}
                </Button>
            </div>
        </div>
    );
}

const CommercialManagerEmails = new Set([
    'humberto.ferrari@bucklerfit.com',
    'daniel.reis@bucklerfit.com',
]);

type CascadeSyncStats = {
    deals: number;
    records_created: number;
    records_updated: number;
    skipped_locked?: number;
};

function toastCascadeSync(t: Translate, sync?: CascadeSyncStats) {
    if (!sync || sync.deals < 1) {
        return;
    }
    toast.success(t('v2.commissioning.sales_teams.sync_ok', {
        deals: sync.deals,
        created: sync.records_created,
        updated: sync.records_updated,
    }));
}

function AssignmentTimeline() {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const { data, isLoading, isError } = useQuery<{ data: SellerAssignmentHistory[] }>({
        queryKey: ['commissioning', 'assignment-history'],
        queryFn: () => api.get('/commissioning/sales-teams/assignment-history').then((r) => r.data),
    });

    const rows = useMemo(() => {
        const all = data?.data ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return all;
        return all.filter((seller) => (seller.user_name ?? '').toLowerCase().includes(q));
    }, [data, search]);

    return (
        <section className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                        {t('v2.commissioning.sales_teams.timeline_title')}
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">{t('v2.commissioning.sales_teams.timeline_help')}</p>
                </div>
                <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 sm:max-w-xs dark:border-zinc-700">
                    <PiMagnifyingGlassBold className="size-4 shrink-0 text-gray-400" />
                    <input
                        className="flex-1 bg-transparent text-sm outline-none dark:text-zinc-200"
                        placeholder={t('v2.commissioning.sales_teams.timeline_search')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>
            {isLoading && <LoadingState variant="table" />}
            {isError && (
                <p className="text-sm text-red-600">{t('v2.commissioning.sales_teams.timeline_error')}</p>
            )}
            {!isLoading && !isError && rows.length === 0 && (
                <p className="text-sm text-gray-400">{t('v2.commissioning.sales_teams.timeline_empty')}</p>
            )}
            {!isLoading && !isError && rows.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                            <tr>
                                <th className="px-4 py-2.5 text-left">{t('v2.commissioning.sales_teams.col_seller')}</th>
                                <th className="px-4 py-2.5 text-left">{t('v2.commissioning.sales_teams.col_team')}</th>
                                <th className="px-4 py-2.5 text-left">{t('v2.commissioning.sales_teams.col_manager')}</th>
                                <th className="px-4 py-2.5 text-left">{t('v2.commissioning.sales_teams.period_from')}</th>
                                <th className="px-4 py-2.5 text-right" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                            {rows.map((seller) => {
                                const current = currentAssignment(seller.periods);
                                const history = seller.periods.filter((p) => !p.is_gap);
                                const showTrail = history.length > 1;

                                return (
                                    <tr key={seller.user_id} className="align-top hover:bg-gray-50/70 dark:hover:bg-zinc-800/40">
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-gray-900 dark:text-zinc-100">{seller.user_name}</p>
                                            {showTrail && (
                                                <p className="mt-1 flex flex-wrap items-center gap-x-1 text-[11px] leading-4 text-gray-400">
                                                    {history.map((period, idx) => (
                                                        <span key={`${seller.user_id}-${period.from}-${idx}`} className="inline-flex items-center gap-1">
                                                            {idx > 0 && <span aria-hidden>→</span>}
                                                            <span>
                                                                {period.team_name ?? t('v2.commissioning.sales_teams.period_no_team')}
                                                                {' · '}
                                                                {period.manager?.name ?? t('v2.commissioning.sales_teams.no_manager')}
                                                            </span>
                                                        </span>
                                                    ))}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-zinc-300">
                                            {current?.team_name ?? t('v2.commissioning.sales_teams.period_no_team')}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-zinc-300">
                                            {current?.manager?.name ?? t('v2.commissioning.sales_teams.no_manager')}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {current ? periodRangeLabel(current, t) : '—'}
                                            {showTrail && (
                                                <Badge className="ml-2 bg-gray-100 text-[10px] font-medium text-gray-600 dark:bg-zinc-800 dark:text-zinc-300">
                                                    {t('v2.commissioning.sales_teams.timeline_periods_count', { count: history.length })}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                to={`/commissioning/teams/sellers/${seller.user_id}/periods`}
                                                className="text-xs font-medium text-orange-600 hover:text-orange-700"
                                            >
                                                {t('v2.commissioning.sales_teams.manage_periods')}
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

export default function SalesTeamsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState<SalesTeam | null>(null);

    const { data: teamsData, isLoading } = useQuery<{ data: SalesTeam[] }>({
        queryKey: ['commissioning', 'sales-teams'],
        queryFn: () => api.get('/commissioning/sales-teams').then((r) => r.data),
    });

    const { data: rosterData } = useQuery<{ data: RosterUser[] }>({
        queryKey: ['commissioning', 'sales-roster'],
        queryFn: () => api.get('/commissioning/sales-teams/roster').then((r) => r.data),
    });

    const teams = teamsData?.data ?? [];
    const roster = rosterData?.data ?? [];
    const directorDefaultId = roster.find((u) => u.email === 'paulo.benicio@bucklerfit.com')?.id ?? null;

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['commissioning', 'sales-teams'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'sales-roster'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'assignment-history'] });
        qc.invalidateQueries({ queryKey: ['commissioning', 'org-chart'] });
    };

    const createMutation = useMutation({
        mutationFn: (d: unknown) => api.post('/commissioning/sales-teams', d),
        onSuccess: (res) => {
            toast.success(t('v2.commissioning.sales_teams.created'));
            toastCascadeSync(t, res.data?.cascade_sync);
            invalidate();
            setAdding(false);
        },
        onError: (err: unknown) => {
            const msg = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data;
            toast.error(msg?.errors?.members?.[0] ?? msg?.message ?? t('v2.commissioning.sales_teams.create_error'));
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, ...d }: { id: number } & Record<string, unknown>) => api.put(`/commissioning/sales-teams/${id}`, d),
        onSuccess: (res) => {
            toast.success(t('v2.commissioning.sales_teams.updated'));
            toastCascadeSync(t, res.data?.cascade_sync);
            invalidate();
            setEditing(null);
        },
        onError: (err: unknown) => {
            const msg = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }).response?.data;
            toast.error(msg?.errors?.members?.[0] ?? msg?.message ?? t('v2.commissioning.sales_teams.update_error'));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/sales-teams/${id}`),
        onSuccess: () => { toast.success(t('v2.commissioning.sales_teams.removed')); invalidate(); },
    });

    const commercialTeams = teams.filter((row) => row.commission_mode === 'standard');
    const specialTeams = teams.filter((row) => row.commission_mode === 'fixed_team');

    return (
        <div className="space-y-6">
            <PageHeader
                title={t('v2.commissioning.sales_teams.title')}
                subtitle={t('v2.commissioning.sales_teams.subtitle')}
            >
                <Link
                    to="/commissioning/org-chart"
                    className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:border-orange-300 hover:text-orange-600"
                >
                    <PiTreeStructureBold className="h-4 w-4" />
                    {t('v2.commissioning.sales_teams.org_chart')}
                </Link>
                <Button
                    onClick={() => {
                        setEditing(null);
                        setAdding(true);
                    }}
                    className="flex items-center gap-2"
                >
                    <PiPlusBold className="h-4 w-4" /> {t('v2.commissioning.sales_teams.new_team')}
                </Button>
            </PageHeader>

            {isLoading && <LoadingState variant="cards" />}

            {commercialTeams.length > 0 && (
                <section className="space-y-3">
                    <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t('v2.commissioning.sales_teams.commercial')}</h2>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {commercialTeams.map((team) => (
                            <TeamCard
                                key={team.id}
                                team={team}
                                onEdit={() => {
                                    setAdding(false);
                                    setEditing(team);
                                }}
                                onDelete={() => { if (confirm(t('v2.commissioning.sales_teams.remove_confirm'))) deleteMutation.mutate(team.id); }}
                            />
                        ))}
                    </div>
                </section>
            )}

            {specialTeams.length > 0 && (
                <section className="space-y-3">
                    <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500">{t('v2.commissioning.sales_teams.special')}</h2>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {specialTeams.map((team) => (
                            <TeamCard
                                key={team.id}
                                team={team}
                                onEdit={() => {
                                    setAdding(false);
                                    setEditing(team);
                                }}
                                onDelete={() => { if (confirm(t('v2.commissioning.sales_teams.remove_confirm'))) deleteMutation.mutate(team.id); }}
                            />
                        ))}
                    </div>
                </section>
            )}

            {teams.length === 0 && !isLoading && (
                <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                    {t('v2.commissioning.sales_teams.empty')}
                </div>
            )}

            <AssignmentTimeline />

            <V2Modal
                open={adding || editing !== null}
                onClose={() => {
                    setAdding(false);
                    setEditing(null);
                }}
                title={editing ? t('v2.commissioning.sales_teams.edit_team') : t('v2.commissioning.sales_teams.new_team')}
                description={t('v2.commissioning.sales_teams.form_help')}
                size="5xl"
            >
                {(adding || editing) && (
                    <TeamForm
                        key={editing?.id ?? 'new'}
                        initial={editing ?? undefined}
                        roster={roster}
                        directorDefaultId={directorDefaultId}
                        onSave={(d) => {
                            if (editing) {
                                updateMutation.mutate({ id: editing.id, ...d });
                                return;
                            }
                            createMutation.mutate(d);
                        }}
                        onCancel={() => {
                            setAdding(false);
                            setEditing(null);
                        }}
                        loading={createMutation.isPending || updateMutation.isPending}
                    />
                )}
            </V2Modal>
        </div>
    );
}

function TeamCard({
    team: teamRow,
    onEdit,
    onDelete,
}: {
    team: SalesTeam;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { t } = useI18n();
    // Apenas períodos abertos para exibir no card
    const sellers = teamRow.members.filter((m) =>
        (m.commission_role === 'vendedor' || m.commission_role === 'sdr') && !m.effective_to,
    );

    return (
        <div className={`rounded-xl border bg-white p-5 dark:bg-zinc-900 ${!teamRow.active ? 'border-dashed opacity-60' : 'border-gray-200 dark:border-zinc-700'}`}>
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                            <PiUsersBold className="h-4 w-4 text-orange-500" />
                            {teamRow.name}
                        </h3>
                        <Badge className="bg-gray-100 text-gray-700">
                            {teamRow.commission_mode === 'fixed_team'
                                ? t('v2.commissioning.sales_teams.mode_fixed_short')
                                : t('v2.commissioning.sales_teams.mode_standard_short')}
                        </Badge>
                        {!teamRow.active && (
                            <span className="text-xs text-gray-400">{t('v2.commissioning.sales_teams.inactive')}</span>
                        )}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-zinc-400">
                        <p className="flex items-center gap-2">
                            {teamRow.manager && <UserAvatar name={teamRow.manager.name} avatarUrl={teamRow.manager.avatar_url} size="sm" />}
                            <span>
                                <span className="text-xs text-gray-400">{t('v2.commissioning.sales_teams.manager')}: </span>
                                <strong className="text-gray-800 dark:text-zinc-200">{teamRow.manager?.name ?? '—'}</strong>
                            </span>
                        </p>
                        {teamRow.director && (
                            <p className="flex items-center gap-2">
                                <UserAvatar name={teamRow.director.name} avatarUrl={teamRow.director.avatar_url} size="sm" />
                                <span>
                                    <span className="text-xs text-gray-400">{t('v2.commissioning.sales_teams.director')}: </span>
                                    <strong className="text-gray-800 dark:text-zinc-200">{teamRow.director.name}</strong>
                                </span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 gap-1">
                    <button type="button" onClick={onEdit} className="rounded-lg p-2 text-gray-500 hover:bg-orange-50 hover:text-orange-600">
                        <PiPencilSimpleBold className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={onDelete} className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600">
                        <PiTrashBold className="h-4 w-4" />
                    </button>
                </div>
            </div>
            {sellers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {sellers.map((m) => (
                        <Link
                            key={m.id}
                            to={`/commissioning/teams/sellers/${m.user_id}/periods`}
                            className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-1 text-xs hover:bg-orange-50 dark:bg-zinc-800 dark:hover:bg-orange-950/40"
                        >
                            <UserAvatar name={m.user_name} avatarUrl={m.user_avatar_url} size="sm" />
                            <span className="font-medium">{m.user_name}</span>
                            <span className="text-gray-400">·</span>
                            <span className="text-gray-500">{memberSinceLabel(m.effective_from, t)}</span>
                        </Link>
                    ))}
                </div>
            ) : (
                <p className="text-xs italic text-gray-400">{t('v2.commissioning.sales_teams.no_sellers_linked')}</p>
            )}
        </div>
    );
}
